import { memo, useRef } from 'react';
import { TableProps } from './types';
import { SortIcon } from './SortIcon';

const STRIPE_BG_OPAQUE = '#F6F8F9';
const ROW_HEIGHT = 48;

function TableComponent<T>({
  columns,
  data,
  loading = false,
  loadingRows = 5,
  stickyHeader = false,
  emptyMessage = 'Brak danych',
  getRowKey,
  getRowClassName,
  getStickyCellZIndex,
  tableContainerStyle,
  tableBodyStyle,
  onSort,
  sortColumn,
  sortDirection,
}: TableProps<T>) {
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Build the CSS Grid track for each column. Precedence:
  //   1. `gridTrack` — explicit override, used verbatim.
  //   2. `minWidth` / `maxWidth` (either or both) — wrap in minmax(), with
  //      `width` (if present) acting as the preferred upper bound.
  //   3. `width` alone — fixed track (e.g. '80px' for an actions column).
  //   4. Fallback — minmax(140px, 1fr), same as before.
  const gridTemplate = columns
    .map((col) => {
      if (col.gridTrack) {
        return col.gridTrack;
      }
      if (col.minWidth !== undefined || col.maxWidth !== undefined) {
        const min = col.minWidth ?? '0';
        const max = col.maxWidth ?? col.width ?? '1fr';
        return `minmax(${min}, ${max})`;
      }
      if (col.width) {
        return col.width;
      }
      return 'minmax(140px, 1fr)';
    })
    .join(' ');

  const rowGridStyle = {
    display: 'grid',
    gridTemplateColumns: gridTemplate,
    minHeight: ROW_HEIGHT,
    alignItems: 'center',
  } as const;

  const tableBodyContent = loading ? (
    Array.from({ length: loadingRows }).map((_, idx) => {
      const isEven = idx % 2 === 0;
      return (
        <div
          key={idx}
          className="rounded overflow-visible"
          style={rowGridStyle}
          data-testid={`table-skeleton-row-${idx}`}
        >
          {columns.map((col, i) => {
            const isStickyRight = col.stickyRight === true;
            const cellBg = isEven ? 'white' : 'rgba(232,238,240,0.4)';
            const stickyCellBg = isEven ? '#ffffff' : STRIPE_BG_OPAQUE;
            return (
              <div
                key={String(col.key)}
                className="flex items-center min-w-0"
                style={{
                  minHeight: ROW_HEIGHT,
                  paddingLeft: 20,
                  paddingRight: 20,
                  backgroundColor: isStickyRight ? stickyCellBg : cellBg,
                  borderTopLeftRadius: i === 0 ? 4 : undefined,
                  borderBottomLeftRadius: i === 0 ? 4 : undefined,
                  borderTopRightRadius:
                    i === columns.length - 1 ? 4 : undefined,
                  borderBottomRightRadius:
                    i === columns.length - 1 ? 4 : undefined,
                  ...(isStickyRight && {
                    position: 'sticky',
                    right: 0,
                    zIndex: 11,
                  }),
                }}
                data-testid={`table-skeleton-cell-${idx}-${i}`}
              >
                {i === 0 ? (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-300 animate-pulse" />
                    <div className="h-4 bg-gray-300 rounded w-32 animate-pulse" />
                  </div>
                ) : i === 3 ? (
                  <div className="h-6 bg-gray-300 rounded-full w-24 animate-pulse" />
                ) : (
                  <div className="h-4 bg-gray-300 rounded w-24 animate-pulse" />
                )}
              </div>
            );
          })}
        </div>
      );
    })
  ) : data.length === 0 ? (
    <div className="text-center py-8 text-[#474A4C]" data-testid="table-empty">
      {emptyMessage}
    </div>
  ) : (
    data.map((row, idx) => {
      const rowKey = getRowKey ? getRowKey(row, idx) : idx;
      const isEven = idx % 2 === 0;
      const rowClassName = getRowClassName ? getRowClassName(row, idx) : '';
      return (
        <div
          key={rowKey}
          className={`rounded ${rowClassName}`.trim()}
          style={rowGridStyle}
          data-testid={`table-row-${idx}`}
          role="row"
          aria-rowindex={idx + 2}
        >
          {columns.map((col, i) => {
            const isStickyRight = col.stickyRight === true;
            const stickyCellBg = isEven ? '#ffffff' : STRIPE_BG_OPAQUE;
            const stickyZ =
              isStickyRight && getStickyCellZIndex
                ? getStickyCellZIndex(row, idx, String(col.key))
                : undefined;
            return (
              <div
                key={String(col.key)}
                className={`flex items-center min-w-0 ${
                  isStickyRight || col.noTruncate
                    ? 'overflow-visible'
                    : 'overflow-hidden'
                } ${isEven ? 'bg-white' : 'bg-[rgba(232,238,240,0.4)]'}`}
                data-testid={`table-cell-${idx}-${String(col.key)}`}
                role="gridcell"
                style={{
                  minHeight: ROW_HEIGHT,
                  paddingLeft: 20,
                  paddingRight: 20,
                  borderTopLeftRadius: i === 0 ? 4 : undefined,
                  borderBottomLeftRadius: i === 0 ? 4 : undefined,
                  borderTopRightRadius:
                    i === columns.length - 1 ? 4 : undefined,
                  borderBottomRightRadius:
                    i === columns.length - 1 ? 4 : undefined,
                  ...(isStickyRight && {
                    position: 'sticky',
                    right: 0,
                    zIndex: stickyZ ?? 11,
                    backgroundColor: stickyCellBg,
                  }),
                }}
              >
                <div
                  className={`text-sm leading-5 w-full min-w-0 ${
                    isStickyRight || col.noTruncate
                      ? 'overflow-visible'
                      : 'overflow-hidden'
                  }`}
                >
                  {col.render ? (
                    col.render(row, idx)
                  ) : (
                    <span
                      className="overflow-hidden text-ellipsis whitespace-nowrap block"
                      title={String(row[col.key as keyof T])}
                    >
                      {String(row[col.key as keyof T])}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    })
  );

  return (
    <div
      ref={tableContainerRef}
      className={`w-full min-w-0 ${stickyHeader ? 'flex flex-col' : ''}`}
      style={{ ...tableContainerStyle }}
      data-testid="table-container"
      role="grid"
      aria-label="Data table"
      aria-rowcount={data.length + 1}
    >
      {/* Header row */}
      <div
        className={`shrink-0 ${stickyHeader ? 'sticky top-0' : ''}`}
        style={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          minHeight: ROW_HEIGHT,
          alignItems: 'center',
          ...(stickyHeader
            ? { backgroundColor: '#F2F4F5', borderRadius: 4, zIndex: 30 }
            : undefined),
        }}
        data-testid="table-header"
        role="row"
        aria-rowindex={1}
      >
        {columns.map((col, i) => {
          const isStickyRight = col.stickyRight === true;
          const isLastColumn = i === columns.length - 1;
          return (
            <div
              key={String(col.key)}
              className={`flex items-center min-w-0 bg-[#F2F4F5] ${
                col.noTruncate ? 'overflow-visible' : 'overflow-hidden'
              }`}
              data-testid={`table-header-cell-${String(col.key)}`}
              role="columnheader"
              style={{
                minHeight: ROW_HEIGHT,
                paddingLeft: 20,
                paddingRight: 20,
                paddingTop: 8,
                paddingBottom: 8,
                gap: 8,
                pointerEvents: 'none',
                justifyContent:
                  isLastColumn || col.centerHeader ? 'center' : 'flex-start',
                borderTopLeftRadius: i === 0 ? 4 : undefined,
                borderBottomLeftRadius: i === 0 ? 4 : undefined,
                borderTopRightRadius: isLastColumn ? 4 : undefined,
                borderBottomRightRadius: isLastColumn ? 4 : undefined,
                ...(isStickyRight && {
                  position: 'sticky',
                  right: 0,
                  zIndex: 10,
                  backgroundColor: '#F2F4F5',
                }),
              }}
            >
              <div
                className={`flex items-center min-w-0 flex-1 gap-2 ${
                  isLastColumn || isStickyRight || col.centerHeader
                    ? 'justify-center'
                    : ''
                }`}
              >
                <div
                  className="text-[#474A4C] font-medium"
                  style={{
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                    letterSpacing: '0.1px',
                  }}
                >
                  {col.header}
                </div>
                {col.showSortIcon &&
                  (() => {
                    const colKeyStr = String(col.key);
                    return (
                      <div
                        className="relative flex-shrink-0"
                        aria-hidden={!onSort}
                        style={{ pointerEvents: 'auto' }}
                      >
                        {onSort ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSort(colKeyStr);
                            }}
                            className="flex items-center justify-center rounded p-0.5 hover:bg-black/8 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] cursor-pointer"
                            aria-label={`Sortuj według ${col.header}`}
                            data-testid={`table-sort-${colKeyStr}`}
                          >
                            <SortIcon
                              column={colKeyStr}
                              sortColumn={sortColumn ?? null}
                              sortDirection={sortDirection ?? 'desc'}
                            />
                          </button>
                        ) : (
                          <div aria-hidden>
                            <SortIcon
                              column={colKeyStr}
                              sortColumn={sortColumn ?? null}
                              sortDirection={sortDirection ?? 'desc'}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })()}
              </div>
            </div>
          );
        })}
      </div>

      <div
        ref={tableBodyRef}
        className={
          stickyHeader
            ? 'flex flex-col min-h-0 min-w-0'
            : 'flex flex-col min-h-0'
        }
        data-testid="table-body"
        style={tableBodyStyle}
      >
        {tableBodyContent}
      </div>
    </div>
  );
}

export const Table = memo(TableComponent) as typeof TableComponent;
